import { PropsWithChildren, useState } from 'react';
import cn from 'classnames';
import { ShieldCheck, LogOut, UserRound, Settings, BookOpenText, Headset } from 'lucide-react';
import { Button } from '@leadsnavi/ui-kit';
import Avatar from '@/common/biz/user-avatar';
import { Popover, PopoverTrigger, PopoverContent } from '@leadsnavi/ui-kit';
import useUserProfileStore from '@/stores/user-profile';
import useUserStore from '@/stores/userStore';
import useLogout from '@/hooks/useLogout';
import { PagePath } from '@/routes/paths';

function MenuItem(props: PropsWithChildren & { href?: string; onClick?: () => void }) {
  if (props.href) {
    return (
      <a
        href={props.href}
        target="_blank"
        className="px-2 py-2 flex items-center gap-2 select-none cursor-pointer text-[#262626] rounded-sm transition hover:bg-[#E5E5E5]/45"
        onClick={props.onClick}
      >
        {props.children}
      </a>
    );
  }

  return (
    <div
      className="px-2 py-2 flex items-center gap-2 select-none cursor-pointer text-[#262626] rounded-sm transition hover:bg-[#E5E5E5]/45"
      onClick={props.onClick}
    >
      {props.children}
    </div>
  );
}

export default function AvatarPopover(props: PropsWithChildren) {
  const { showDialog } = useUserProfileStore();
  const { user, displayName } = useUserStore();
  const { handleLogoutWithConfirm } = useLogout();
  const [open, setOpen] = useState(false);

  return (
    <Popover trigger="hover" onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn(open && 'bg-[#E5E5E5]/45', 'hover:bg-[#E5E5E5]/45 rounded-sm')}> {props.children}</div>
      </PopoverTrigger>
      <PopoverContent side="right" sideOffset={24} className="border-none shadow-none p-0">
        <div className="rounded-[12px] mb-4 bg-[linear-gradient(186deg,#F7F7F7_32.28%,#F0FAFC_118.31%)] border border-[#fff] py-4 px-2 shadow-[0_25px_50px_-12px_rgba(0,_0,_0,_0.12)]">
          <div className={cn('py-3 px-2 flex items-center gap-2 select-none rounded-sm transition')}>
            <Avatar className="flex-none" size={44} />
            <div className="min-w-0">
              <div className="text-base font-medium truncate text-[#262626]">{displayName}</div>
              <div className="text-xs truncate text-[#525252]">{user?.email || ''}</div>
            </div>
          </div>
          <div className="my-2 bg-[#E1E3E8] h-[1px]"></div>
          <div className="flex flex-col gap-1 mb-3">
            <MenuItem onClick={() => showDialog('account')}>
              <ShieldCheck size={16} color="#525252" />
              <span>Account</span>
            </MenuItem>
            <MenuItem onClick={() => showDialog('profile')}>
              <UserRound size={16} color="#525252" />
              <span>Profile</span>
            </MenuItem>
            <MenuItem onClick={() => showDialog('settings')}>
              <Settings size={16} color="#525252" />
              <span>Settings</span>
            </MenuItem>
            <MenuItem href="https://support.easesourcing.com/portal/en/home">
              <BookOpenText size={16} color="#525252" />
              <span>Help Center</span>
            </MenuItem>
            <MenuItem href={PagePath.contactUs}>
              <Headset size={16} color="#525252" />
              <span>Contact Us</span>
            </MenuItem>
          </div>

          <div>
            <Button
              variant="outline"
              size="sm"
              className="w-full h-[36px]"
              onClick={() => handleLogoutWithConfirm(true)}
            >
              <LogOut size={16} color="#525252" />
              Log out
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
